import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../../services/authentication/supabaseService';
import { useAuth } from '../../../services/authentication/authContext';
import { Header } from '../landingpage/sections/Header';
import { useSubscription } from '../../../hooks/useSubscription';
import { customerLandingPath, customerOnboardingStage } from '../../../services/authentication/customerOnboarding';
import { isImpersonating } from '../../../stores/appImpersonationStore';
import { getSupportReturnPath } from '../../../services/support/supportReturnPath';
import { getRecipeViewerReturnPath } from '../recipeViewer/recipeViewerAccess';
import { getMenuChangeReturnPath } from '../../../services/menuChange/menuChangeLinks';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { user, backendUser, accountSettings, loading } = useAuth();
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const resendAfter = useRef(0);
  const busy = useRef(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { isActive, loading: subscriptionLoading } = useSubscription();
  const supportReturnTo = getSupportReturnPath(location.state?.supportReturnTo);
  const recipeViewerReturnTo = getRecipeViewerReturnPath(location.state?.recipeViewerReturnTo);
  const menuChangeReturnTo = getMenuChangeReturnPath(location.state?.menuChangeReturnTo);
  // Supabase restores the confirmation session before our customer profile arrives.
  // Keep credentials hidden through that handoff and the subsequent redirect.
  const openingAccount = loading || !!user;

  useEffect(() => {
    // Same branch as the submit handler below, so a session arriving via the
    // signup-confirmation link (emailRedirectTo=/login) lands on /payment too.
    // Wait for the subscription check, or a paying user flashes through /payment.
    if (!loading && user && backendUser && !subscriptionLoading) {
      const landing = customerLandingPath(backendUser, accountSettings, isActive, isImpersonating());
      navigate(customerOnboardingStage(backendUser, isActive, isImpersonating()) || landing === '/setup' ? landing : menuChangeReturnTo ?? recipeViewerReturnTo ?? supportReturnTo ?? landing);
    }

    // Check for success message from password reset
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the state to prevent showing the message on page refresh
      window.history.replaceState({}, document.title);
    }
  }, [user, loading, subscriptionLoading, isActive, navigate, location, supportReturnTo, recipeViewerReturnTo, menuChangeReturnTo, accountSettings, backendUser]);

  useEffect(() => {
    const params = new URLSearchParams(location.hash.replace(/^#/, ''));
    const query = new URLSearchParams(location.search);
    if (params.has('error') || query.has('error')) setError(t('managedSignup.confirmationExpired'));
  }, [location.hash, location.search, t]);

  useEffect(() => {
    if (!cooldown) return;
    const timer = window.setTimeout(() => setCooldown(Math.max(0, Math.ceil((resendAfter.current - Date.now()) / 1000))), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const sendConfirmation = async () => {
    if (Date.now() < resendAfter.current) return;
    resendAfter.current = Date.now() + 60000;
    setCooldown(60);
    setError(null);
    setSuccessMessage(null);
    const { error: resendError } = await supabase.auth.resend({
      type: 'signup', email: email.trim(), options: { emailRedirectTo: `${window.location.origin}/login?lang=${i18n.language.startsWith('nl') ? 'nl' : 'en'}` },
    });
    if (resendError) setError(t('managedSignup.confirmationFailed'));
    else setSuccessMessage(t('auth.confirmEmailSentDescription', { email: email.trim() }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy.current) return;
    busy.current = true; setSubmitting(true); setError(null);
    try {
      const { error: loginError } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (loginError?.code === 'email_not_confirmed') {
        setCheckingEmail(true);
        setPassword('');
        await sendConfirmation();
      } else if (loginError) setError(loginError.message);
    } catch { setError(t('managedSignup.loginFailed')); }
    finally { busy.current = false; setSubmitting(false); }
  };

  return (
    <div className="relative overflow-hidden min-h-screen bg-landingPageBG text-white dark:bg-landingPageBG dark:text-white">
      <Header />
      <div className="flex justify-center px-5 pt-20">
        <div className="relative z-10 w-full max-w-md h-fit">
          <div className="rounded-2xl border border-white/10 p-8 bg-white/5 backdrop-blur-sm dark:border-white/10 dark:bg-white/5">
            {openingAccount ? (
              <div role="status" aria-busy="true" className="flex flex-col items-center gap-4 py-6 text-center">
                <Loader2 className="h-6 w-6 animate-spin text-white/70 dark:text-white/70" aria-hidden="true" />
                <p>{t('common.loading')}</p>
              </div>
            ) : <>
            <h1 className="text-2xl font-semibold text-white mb-6 dark:text-white">{t('auth.welcomeBack')}</h1>
            {successMessage && (
              <div className="mb-4 p-3 rounded-xl bg-green-500/10 border border-green-500/30 text-green-300 text-sm dark:bg-green-500/10 dark:border-green-500/30 dark:text-green-300">
                {successMessage}
              </div>
            )}
            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-300">
                {error}
              </div>
            )}
            {checkingEmail ? <div className="space-y-4">
              <h2 className="font-semibold">{t('managedSignup.checkEmail')}</h2>
              <p>{t('managedSignup.confirmationHelp')}</p>
              <button disabled={submitting || cooldown > 0} className="min-h-[44px] w-full rounded-xl p-3 border border-white/20 dark:border-white/20 disabled:opacity-50" onClick={() => void sendConfirmation().catch(() => setError(t('managedSignup.confirmationFailed')))}>{cooldown > 0 ? t('managedSignup.resendIn', { seconds: cooldown }) : t('managedSignup.resend')}</button>
              <button className="min-h-[44px] underline" onClick={() => { setCheckingEmail(false); setSuccessMessage(null); setError(null); }}>{t('managedSignup.backToLogin')}</button>
            </div> : <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  type="email"
                  required autoComplete="username" placeholder={t('managedSignup.email')}
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-white/20 rounded-xl bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent dark:border-white/20 dark:bg-white/10 dark:text-white dark:placeholder-white/50 dark:focus:ring-accent dark:focus:border-transparent"
                />
              </div>
              <div>
                <input
                  type="password"
                  required autoComplete="current-password" placeholder={t('managedSignup.password')}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-white/20 rounded-xl bg-white/10 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent dark:border-white/20 dark:bg-white/10 dark:text-white dark:placeholder-white/50 dark:focus:ring-accent dark:focus:border-transparent"
                />
              </div>
              <div className="text-right">
                <a href="/forgot-password" className="text-sm text-white/70 hover:text-white transition dark:text-white/70 dark:hover:text-white">
                  {t('auth.forgotPassword')}?
                </a>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-6 py-3 text-base font-semibold bg-accent text-white hover:bg-accent/90 rounded-2xl transition dark:bg-accent dark:text-white dark:hover:bg-accent/90"
              >
                {t('auth.login')}
              </button>
            </form>}
            <p className="mt-6 text-center text-sm text-white/70 dark:text-white/70">
              {t('auth.dontHaveAccount')}{' '}
              <a href="/signup" className="text-white font-medium hover:underline dark:text-white">
                {t('auth.signUp')}
              </a>
            </p>
            </>}
          </div>
        </div>
      </div>
    </div>
  );
}
